import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Search, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Plus, X, MessageSquarePlus,
  CheckCircle2, Pencil, Trash2, Lightbulb, Rocket, Archive, MessageCircle, Send,
} from 'lucide-react';
import { roadmapApi, backlogApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import PublicHeader from '../components/PublicHeader';
import IdeaDialog from '../components/IdeaDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

/**
 * Backlog pubblico — layout Kanban a colonne.
 *
 * Le colonne sono lo stato dell'idea (In valutazione / In corso / Completate),
 * così l'utente percepisce subito il flusso del prodotto. Niente TOC laterale
 * né tab di filtro: lo stato È il filtro. Il vote button rimane ben visibile
 * a sinistra di ogni card.
 *
 * - Pubblico: 3 colonne, no priorità mostrata, vote + submit liberi.
 * - OPS loggato: 4ª colonna "Scartate" + chip priorità cliccabile + dropdown
 *   stato + edit/delete inline su ogni card.
 *
 * Mobile: stack verticale (1 colonna). I count nei header indicano la
 * dimensione di ogni colonna. Le colonne hanno scroll interno con max-height
 * limitata per evitare che una sezione lunga schiacci le altre.
 */

const VOTER_KEY = 'storeops_voter_id';

function getOrCreateVoterId() {
  try {
    let id = window.localStorage.getItem(VOTER_KEY);
    if (!id) {
      id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`)
        .replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 64);
      window.localStorage.setItem(VOTER_KEY, id);
    }
    return id;
  } catch {
    return `mem-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

function fmtRelative(iso) {
  if (!iso) return '';
  const ts = typeof iso === 'string' ? Date.parse(iso) : new Date(iso).getTime();
  const diff = Math.floor((Date.now() - ts) / 86400000);
  if (diff <= 0) return 'oggi';
  if (diff === 1) return 'ieri';
  if (diff < 7) return `${diff} giorni fa`;
  if (diff < 30) return `${Math.floor(diff / 7)} settimane fa`;
  if (diff < 365) return `${Math.floor(diff / 30)} mesi fa`;
  return `${Math.floor(diff / 365)} anni fa`;
}

const COLUMNS = [
  {
    id: 'proposed',
    label: 'In valutazione',
    description: 'Idee in attesa di triage',
    icon: Lightbulb,
    accent: 'text-amber-700 bg-amber-50 border-amber-200',
    headerBar: 'bg-amber-400',
  },
  {
    id: 'in_progress',
    label: 'In corso',
    description: 'Stiamo lavorando a questo',
    icon: Rocket,
    accent: 'text-blue-700 bg-blue-50 border-blue-200',
    headerBar: 'bg-blue-500',
  },
  {
    id: 'done',
    label: 'Completate',
    description: 'Recentemente rilasciate',
    icon: CheckCircle2,
    accent: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    headerBar: 'bg-emerald-500',
  },
];

const OPS_EXTRA_COLUMN = {
  id: 'discarded',
  label: 'Scartate',
  description: 'Non procediamo (visibile solo a OPS)',
  icon: Archive,
  accent: 'text-slate-500 bg-slate-50 border-slate-200',
  headerBar: 'bg-slate-300',
};

const ALL_STATUSES = [
  { value: 'proposed',    label: 'In valutazione' },
  { value: 'in_progress', label: 'In corso' },
  { value: 'done',        label: 'Completata' },
  { value: 'discarded',   label: 'Scartata' },
];

const PRIORITIES = ['P0', 'P1', 'P2', 'P3'];
const PRIORITY_CLS = {
  P0: 'bg-red-100 text-red-800 border-red-300',
  P1: 'bg-orange-100 text-orange-800 border-orange-300',
  P2: 'bg-amber-100 text-amber-800 border-amber-300',
  P3: 'bg-slate-100 text-slate-700 border-slate-300',
};

// Pill di stato per il dettaglio idea (popup): colori coerenti con i bordi
// delle colonne Kanban così l'utente collega visivamente popup e board.
const STATUS_PILL = {
  proposed:    { label: 'In valutazione', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  in_progress: { label: 'In corso',       cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  done:        { label: 'Completata',     cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  discarded:   { label: 'Scartata',       cls: 'bg-slate-100 text-slate-500 border-slate-300' },
};

export default function RoadmapPage() {
  const { user } = useAuth();
  const isOps = user?.role === 'operations_manager';

  const [data, setData] = useState({ proposed: [], in_progress: [], done: [], discarded: [] });
  const [loading, setLoading] = useState(true);
  const [voterId] = useState(getOrCreateVoterId);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [voting, setVoting] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [detail, setDetail] = useState(null);   // idea_id da espandere in popup
  // Filtro priorità (OPS-only): set vuoto = nessun filtro = tutte visibili.
  const [priorityFilter, setPriorityFilter] = useState(new Set());
  // Sort mode applicato a `proposed` e `in_progress`. Il nostro Kanban ha 3-4
  // dozzine di idee aperte: cambiando sort si vedono pattern diversi.
  // - 'votes':     più votate (netto upvote-downvote desc) — default storico
  // - 'upvotes':   "più amate" (upvote count desc, ignora i downvote)
  // - 'downvotes': "più discusse/contestate" (downvote count desc)
  // - 'recent':    create_at desc (utili per triage di nuove idee)
  const [sortMode, setSortMode] = useState('votes');
  // Free-text search (idea_5538fdea5842, richiesta di Marco).
  // Filtra TUTTE le colonne su titolo + descrizione + autore + idea_id.
  // Visibile a pubblico e OPS (case-insensitive, substring match).
  const [searchQuery, setSearchQuery] = useState('');
  const normalizedQuery = searchQuery.trim().toLowerCase();
  // Snapshot dell'ordine corrente attivo SOLO mentre il DetailDialog è aperto.
  // Senza snapshot, ogni upvote/downvote/cambio priorità dentro al popup
  // riordina il Kanban sotto: alla chiusura l'utente perde il punto e
  // rivede idee già viste. Lo snapshot si crea quando si apre il popup
  // e si scarta alla chiusura — al successivo refresh la lista si rinfresca
  // con l'ordine basato sui nuovi voti.
  const [frozenOrder, setFrozenOrder] = useState(null);  // { col_id: [idea_id, ...] } | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isOps) {
        const r = await backlogApi.list();
        const ideas = r.data || [];
        const grouped = { proposed: [], in_progress: [], done: [], discarded: [] };
        for (const i of ideas) {
          const upvoters = i.voters || [];
          const downvoters = i.downvoters || [];
          const enriched = {
            ...i,
            // Per OPS il count viene già dal DB (mantenuto coerente dal vote endpoint).
            // Lo ricalcolo come fallback per documenti vecchi senza downvoters.
            votes: upvoters.length - downvoters.length,
            // Conteggi separati per i sort "Più amate" / "Più discusse"
            // (allineati al payload del public roadmap endpoint).
            upvotes_count: upvoters.length,
            downvotes_count: downvoters.length,
            has_voted: voterId && upvoters.includes(voterId),
            has_downvoted: voterId && downvoters.includes(voterId),
          };
          delete enriched.voters;
          delete enriched.downvoters;
          if (grouped[i.status]) grouped[i.status].push(enriched);
        }
        setData(grouped);
      } else {
        const r = await roadmapApi.list(voterId);
        setData({ ...r.data, discarded: [] });
      }
    } finally { setLoading(false); }
  }, [isOps, voterId]);
  useEffect(() => { load(); }, [load]);

  const totalOpen = data.proposed.length + data.in_progress.length;
  const totalVotes = useMemo(
    () => [...data.proposed, ...data.in_progress, ...data.done].reduce((a, i) => a + (i.votes || 0), 0),
    [data]
  );
  const lastShipped = data.done?.[0]?.completed_at;
  const totalIdeas = data.proposed.length + data.in_progress.length + data.done.length + (isOps ? data.discarded.length : 0);

  const columns = useMemo(
    () => (isOps ? [...COLUMNS, OPS_EXTRA_COLUMN] : COLUMNS),
    [isOps]
  );

  // Sorting:
  // - 'done' sempre per `completed_at` desc (cronologia release pubblica).
  // - 'discarded' sempre per `updated_at` desc (OPS-internal).
  // - 'proposed' / 'in_progress' usano `sortMode` (votes / upvotes / downvotes / recent).
  // Quando `frozenOrder` è settato (popup aperto), riusiamo l'ordine fissato
  // anche dopo che `data` cambia, così vote/edit nel popup non riordinano.
  const sortedColumn = useMemo(() => {
    const cache = {};
    // Priority sort: P0=0 (highest), P3=3 (lowest), unset/altre = 99 (last).
    // Tie-break con votes desc così tra due P2 vince la più amata.
    const prioRank = (p) => {
      const n = parseInt(String(p || '').replace(/^P/, ''), 10);
      return isNaN(n) ? 99 : n;
    };
    const sorters = {
      votes:     (a, b) => (b.votes || 0) - (a.votes || 0),
      upvotes:   (a, b) => (b.upvotes_count || 0) - (a.upvotes_count || 0),
      downvotes: (a, b) => (b.downvotes_count || 0) - (a.downvotes_count || 0),
      recent:    (a, b) => (b.created_at || '').localeCompare(a.created_at || ''),
      priority:  (a, b) => {
        const pa = prioRank(a.priority);
        const pb = prioRank(b.priority);
        if (pa !== pb) return pa - pb;
        return (b.votes || 0) - (a.votes || 0); // tie-break
      },
    };
    const openSorter = sorters[sortMode] || sorters.votes;
    for (const col of columns) {
      const arr = [...(data[col.id] || [])];
      // Filtro priorità: applicato a TUTTE le colonne (proposed/in_progress/done/discarded)
      // così OPS può guardare solo le P0/P1 in tutte le fasi del flusso.
      if (priorityFilter.size > 0) {
        for (let i = arr.length - 1; i >= 0; i--) {
          if (!priorityFilter.has(arr[i].priority)) arr.splice(i, 1);
        }
      }
      // Filtro search: idea_5538fdea5842. Match case-insensitive su tutti
      // i campi rilevanti di un'idea. Se la query è vuota, no-op.
      if (normalizedQuery) {
        for (let i = arr.length - 1; i >= 0; i--) {
          const it = arr[i];
          const hay = [
            it.title || '',
            it.description || '',
            it.author_name || '',
            it.idea_id || '',
          ].join(' ').toLowerCase();
          if (!hay.includes(normalizedQuery)) arr.splice(i, 1);
        }
      }
      if (col.id === 'done')           arr.sort((a, b) => (b.completed_at || '').localeCompare(a.completed_at || ''));
      else if (col.id === 'discarded') arr.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
      else                              arr.sort(openSorter);

      // Riapplica l'ordine snapshot se il popup è aperto, mantenendo le
      // nuove idee in fondo (può capitare con auto-refresh in background).
      const frozen = frozenOrder?.[col.id];
      if (frozen) {
        const indexInFrozen = new Map(frozen.map((id, idx) => [id, idx]));
        arr.sort((a, b) => {
          const ia = indexInFrozen.has(a.idea_id) ? indexInFrozen.get(a.idea_id) : 1e9;
          const ib = indexInFrozen.has(b.idea_id) ? indexInFrozen.get(b.idea_id) : 1e9;
          return ia - ib;
        });
      }
      cache[col.id] = arr;
    }
    return cache;
  }, [data, columns, sortMode, priorityFilter, frozenOrder, normalizedQuery]);

  // Conteggio "trovate N idee su M" — utile come feedback quando la query
  // restringe molto; mostriamo solo le colonne pubbliche per non confondere.
  const searchStats = useMemo(() => {
    if (!normalizedQuery) return null;
    const visible = (sortedColumn.proposed?.length || 0) + (sortedColumn.in_progress?.length || 0)
      + (sortedColumn.done?.length || 0) + (sortedColumn.discarded?.length || 0);
    const total = (data.proposed?.length || 0) + (data.in_progress?.length || 0)
      + (data.done?.length || 0) + (data.discarded?.length || 0);
    return { visible, total };
  }, [normalizedQuery, sortedColumn, data]);

  // Lista piatta degli idea_id nell'ordine in cui appaiono sul Kanban
  // (concatenando le colonne nella stessa sequenza con cui sono renderizzate).
  // Serve per le frecce prev/next del DetailDialog: l'utente naviga tra le
  // idee senza chiudere il popup.
  const flatOrderedIds = useMemo(() => {
    const out = [];
    for (const col of columns) for (const it of (sortedColumn[col.id] || [])) out.push(it.idea_id);
    return out;
  }, [columns, sortedColumn]);

  // Trova l'idea attualmente in detail-popup partendo da `detail` (idea_id)
  // e dallo state `data`. Così il popup riflette automaticamente i cambi
  // (voti, edit, status) senza dover gestire stato duplicato.
  const detailIdea = useMemo(() => {
    if (!detail) return null;
    for (const col of ['proposed', 'in_progress', 'done', 'discarded']) {
      const found = (data[col] || []).find(i => i.idea_id === detail);
      if (found) return found;
    }
    return null;
  }, [detail, data]);

  // Calcola index corrente + handler prev/next. Sono `null` quando si è
  // ai bordi della lista, così il popup può disabilitare la freccia.
  const detailIndex = detail ? flatOrderedIds.indexOf(detail) : -1;
  const goPrev = (detailIndex > 0)
    ? () => setDetail(flatOrderedIds[detailIndex - 1])
    : null;
  const goNext = (detailIndex >= 0 && detailIndex < flatOrderedIds.length - 1)
    ? () => setDetail(flatOrderedIds[detailIndex + 1])
    : null;

  /**
   * Voto con direzione (1=up, -1=down). Logica toggle stile Reddit:
   * - click su "up" mentre era up → annulla (nessun voto)
   * - click su "up" mentre era down → switch a up
   * - click su "down" mentre era down → annulla
   * - click su "down" mentre era up → switch a down
   *
   * Optimistic update: aggiusto subito UI e count, rollback su errore.
   */
  const handleVote = async (idea, direction) => {
    if (voting || idea.status === 'done' || idea.status === 'discarded') return;
    setVoting(idea.idea_id);

    // Calcola lo stato successivo (up/down/null) per ottimistic UI
    const wasUp = idea.has_voted;
    const wasDown = idea.has_downvoted;
    const target =
      direction === 1
        ? (wasUp ? null : 'up')
        : (wasDown ? null : 'down');

    // Delta sui counter: aggiorniamo `votes` (netto) ma anche `upvotes_count`
    // e `downvotes_count` separatamente, così i sort "Più amate" / "Più
    // discusse" vedono i numeri aggiornati in tempo reale senza aspettare
    // il refetch.
    let delta = 0;
    let upDelta = 0;
    let downDelta = 0;
    if (wasUp)             { delta -= 1; upDelta -= 1; }
    if (wasDown)           { delta += 1; downDelta -= 1; }
    if (target === 'up')   { delta += 1; upDelta += 1; }
    if (target === 'down') { delta -= 1; downDelta += 1; }

    setData(prev => updateIdeaInPlace(prev, idea.idea_id, (it) => ({
      ...it,
      votes: (it.votes || 0) + delta,
      upvotes_count: (it.upvotes_count || 0) + upDelta,
      downvotes_count: (it.downvotes_count || 0) + downDelta,
      has_voted: target === 'up',
      has_downvoted: target === 'down',
    })));

    try {
      const r = await roadmapApi.vote(idea.idea_id, voterId, direction);
      setData(prev => updateIdeaInPlace(prev, idea.idea_id, (it) => ({
        ...it,
        votes: r.data.votes,
        // Il payload del vote endpoint potrebbe non avere upvotes/downvotes
        // separati su istanze legacy: in tal caso lascio i counter ottimistici
        // (saranno consistenti col delta appena applicato).
        upvotes_count: r.data.upvotes_count ?? (it.upvotes_count || 0),
        downvotes_count: r.data.downvotes_count ?? (it.downvotes_count || 0),
        has_voted: r.data.has_voted,
        has_downvoted: r.data.has_downvoted,
      })));
    } catch (e) {
      // Rollback completo
      setData(prev => updateIdeaInPlace(prev, idea.idea_id, (it) => ({
        ...it,
        votes: (it.votes || 0) - delta,
        upvotes_count: (it.upvotes_count || 0) - upDelta,
        downvotes_count: (it.downvotes_count || 0) - downDelta,
        has_voted: wasUp,
        has_downvoted: wasDown,
      })));
      alert(e.response?.data?.detail || 'Voto fallito, riprova.');
    } finally { setVoting(null); }
  };

  /**
   * Apre il DetailDialog e congela l'ordine attuale del Kanban così
   * upvote/downvote/cambi priorità eseguiti dentro al popup non riordinano
   * le card sotto. Allo svelare del popup l'ordine torna libero.
   */
  const openDetail = (ideaId) => {
    // Catturo lo snapshot prima di aprire, basandomi sul `sortedColumn`
    // attualmente renderizzato.
    const snap = {};
    for (const col of columns) {
      snap[col.id] = (sortedColumn[col.id] || []).map(i => i.idea_id);
    }
    setFrozenOrder(snap);
    setDetail(ideaId);
  };
  const closeDetail = () => {
    setDetail(null);
    setFrozenOrder(null);  // libera l'ordine → il prossimo render risorta
  };

  const handleAdminStatus = async (idea, status) => {
    try { await backlogApi.update(idea.idea_id, { status }); await load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const handleAdminPriority = async (idea, priority) => {
    try { await backlogApi.update(idea.idea_id, { priority }); await load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const handleAdminDelete = async () => {
    if (!confirmDelete) return;
    try { await backlogApi.remove(confirmDelete.idea_id); setConfirmDelete(null); await load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="min-h-screen bg-slate-50/30" data-testid="roadmap-page">
      <PublicHeader />

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="roadmap-main">
        {/* Hero compatto, allineato al respiro del changelog ma più sottile per
            lasciare spazio alle 3-4 colonne sotto. */}
        <div className="flex items-end justify-between gap-6 flex-wrap mb-8 pb-6 border-b border-slate-200">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-2">Backlog pubblico</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Cosa stiamo costruendo
            </h1>
            <p className="text-sm text-slate-500 mt-2 max-w-xl">
              Vota le idee che ti interessano di più, oppure proponine una nuova. Quello che vedi qui guida le nostre prossime release.
            </p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-500 mt-4" data-testid="roadmap-stats">
              <span><strong className="text-slate-900 font-heading text-base mr-1">{totalIdeas}</strong>idee</span>
              <span><strong className="text-slate-900 font-heading text-base mr-1">{totalOpen}</strong>aperte</span>
              <span><strong className="text-slate-900 font-heading text-base mr-1">{totalVotes}</strong>voti</span>
              {lastShipped && (
                <span>Ultima release <span className="text-slate-700 font-medium">{fmtRelative(lastShipped)}</span></span>
              )}
            </div>
          </div>
          <button
            onClick={() => setSubmitOpen(true)}
            className="inline-flex items-center gap-2 bg-black text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-900 transition-colors flex-shrink-0"
            data-testid="roadmap-submit-btn"
          >
            <Plus className="w-4 h-4" /> Proponi un'idea
          </button>
        </div>

        {loading ? (
          <div className="py-24 flex justify-center">
            <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <BacklogToolbar
              isOps={isOps}
              priorityFilter={priorityFilter}
              setPriorityFilter={setPriorityFilter}
              sortMode={sortMode}
              setSortMode={setSortMode}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              searchStats={searchStats}
            />
            <div
              className={`grid gap-5 ${isOps ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}
              data-testid="roadmap-board"
            >
              {columns.map(col => (
                <Column
                  key={col.id}
                  col={col}
                  items={sortedColumn[col.id] || []}
                  onVote={handleVote}
                  votingId={voting}
                  isOps={isOps}
                  onAdminStatus={handleAdminStatus}
                  onAdminPriority={handleAdminPriority}
                  onAdminEdit={(idea) => setEditing(idea)}
                  onAdminDelete={(idea) => setConfirmDelete(idea)}
                  onOpenDetail={(idea) => openDetail(idea.idea_id)}
                />
              ))}
            </div>
          </>
        )}
      </main>

      <SubmitDialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onSaved={async () => { setSubmitOpen(false); await load(); }}
      />

      {/* Popup di dettaglio idea: descrizione full + voti + (per OPS) edit/delete.
          Si apre cliccando il body della card. Vote button continua a funzionare
          dentro il dialog per chi vuole votare durante la lettura. Le frecce
          ◀ ▶ (e Left/Right da tastiera) permettono di scorrere tra le idee
          mantenendo il popup aperto: utile quando si fa triage in sequenza. */}
      <DetailDialog
        idea={detailIdea}
        onClose={closeDetail}
        onVote={(direction) => detailIdea && handleVote(detailIdea, direction)}
        voting={voting === detailIdea?.idea_id}
        isOps={isOps}
        onAdminEdit={() => { if (detailIdea) { setEditing(detailIdea); closeDetail(); } }}
        onAdminDelete={() => { if (detailIdea) { setConfirmDelete(detailIdea); closeDetail(); } }}
        onPrev={goPrev}
        onNext={goNext}
        position={detailIndex >= 0 ? { current: detailIndex + 1, total: flatOrderedIds.length } : null}
      />

      <IdeaDialog
        open={!!editing}
        idea={editing}
        canEditWorkflow={isOps}
        onClose={() => setEditing(null)}
        onSaved={async () => { setEditing(null); await load(); }}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Eliminare l'idea?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDelete?.title}" sarà rimossa definitivamente, voti inclusi. L'azione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAdminDelete}
              className="rounded-none bg-red-600 hover:bg-red-700"
              data-testid="roadmap-delete-confirm"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ------- helpers ------- */

const PRIORITY_OPTIONS = ['P0', 'P1', 'P2', 'P3'];
const SORT_OPTIONS = [
  { id: 'votes',     label: 'Più votate',   hint: 'Net upvote − downvote (default)' },
  { id: 'priority',  label: 'Priorità',     hint: 'P0 → P3, tie-break sui voti' },
  { id: 'upvotes',   label: 'Più amate',    hint: 'Solo upvote, ignora downvote' },
  { id: 'downvotes', label: 'Più discusse', hint: 'Più downvote per primi (triage controversie)' },
  { id: 'recent',    label: 'Recenti',      hint: 'Data di creazione più recente prima' },
];

/**
 * Toolbar above the Kanban with search input (visible to public + OPS),
 * priority filter chips (OPS-only) + sort mode selector. Per il pubblico
 * mostriamo solo la search; OPS vede anche filtri priorità e ordine.
 */
function BacklogToolbar({ isOps, priorityFilter, setPriorityFilter, sortMode, setSortMode, searchQuery, setSearchQuery, searchStats }) {
  const togglePriority = (p) => {
    setPriorityFilter(prev => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p); else next.add(p);
      return next;
    });
  };

  return (
    <div
      className="mb-5 pb-4 border-b border-slate-200 space-y-3"
      data-testid="backlog-toolbar"
    >
      {/* Search input — sempre in cima alla toolbar, full-width su mobile,
          max-w sui desktop per non occupare tutta la riga. Tasto X clear
          appare quando c'è una query. */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1 max-w-2xl">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca per titolo, descrizione, autore o ID idea…"
            className="w-full pl-9 pr-9 py-2 text-sm border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 placeholder:text-slate-400"
            data-testid="backlog-search-input"
            aria-label="Cerca nel backlog"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              data-testid="backlog-search-clear"
              aria-label="Pulisci ricerca"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {searchStats && (
          <span
            className="text-xs text-slate-500 tabular-nums"
            data-testid="backlog-search-stats"
          >
            {searchStats.visible === 0
              ? 'Nessuna idea trovata'
              : `${searchStats.visible} ${searchStats.visible === 1 ? 'idea trovata' : 'idee trovate'} su ${searchStats.total}`}
          </span>
        )}
      </div>

      {/* Filtri OPS-only: priorità + sort. Renderizzati sotto la search
          in una riga separata. Per il pubblico questa sezione non appare. */}
      {isOps && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          {/* Priority filter chips */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              Priorità
            </span>
            <button
              type="button"
              onClick={() => setPriorityFilter(new Set())}
              className={`px-2.5 py-1 text-xs font-medium border transition-colors ${
                priorityFilter.size === 0
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400'
              }`}
              data-testid="priority-filter-all"
            >
              Tutte
            </button>
            {PRIORITY_OPTIONS.map(p => {
              const active = priorityFilter.has(p);
              const tone = {
                P0: 'border-red-300 text-red-700',
                P1: 'border-orange-300 text-orange-700',
                P2: 'border-amber-300 text-amber-700',
                P3: 'border-slate-300 text-slate-600',
              }[p];
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => togglePriority(p)}
                  className={`px-2.5 py-1 text-xs font-medium border transition-colors tabular-nums ${
                    active
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : `bg-white hover:border-slate-400 ${tone}`
                  }`}
                  data-testid={`priority-filter-${p}`}
                >
                  {p}
                </button>
              );
            })}
          </div>

          {/* Sort selector */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              Ordina
            </span>
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              className="text-xs px-3 py-1.5 border border-slate-300 bg-white hover:border-slate-400 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900"
              data-testid="backlog-sort-select"
            >
              {SORT_OPTIONS.map(opt => (
                <option key={opt.id} value={opt.id} title={opt.hint}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

function updateIdeaInPlace(state, ideaId, updater) {
  const apply = (arr) => arr.map(i => i.idea_id === ideaId ? updater(i) : i);
  return {
    proposed: apply(state.proposed),
    in_progress: apply(state.in_progress),
    done: apply(state.done),
    discarded: apply(state.discarded || []),
  };
}

function Column({ col, items, onVote, votingId, isOps, onAdminStatus, onAdminPriority, onAdminEdit, onAdminDelete, onOpenDetail }) {
  const Icon = col.icon;
  return (
    <section
      className="bg-white border border-slate-200 flex flex-col min-h-[200px]"
      data-testid={`roadmap-col-${col.id}`}
    >
      {/* Top bar colorata per riconoscere la colonna a colpo d'occhio */}
      <div className={`h-1 ${col.headerBar}`} />
      <header className="px-4 pt-3 pb-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center justify-center w-6 h-6 border ${col.accent} flex-shrink-0`}>
            <Icon className="w-3.5 h-3.5" strokeWidth={2} />
          </span>
          <h2 className="font-heading font-bold text-sm text-slate-900 tracking-tight">{col.label}</h2>
          <span className="ml-auto text-xs font-mono tabular-nums text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-sm">
            {items.length}
          </span>
        </div>
        <p className="text-[11px] text-slate-400 mt-1.5 leading-tight">{col.description}</p>
      </header>

      {/* Lista delle card. max-height + overflow-y per evitare colonne lunghe
          che spingono in basso le altre. */}
      <div className="flex-1 p-3 space-y-2.5 max-h-[70vh] overflow-y-auto" data-testid={`roadmap-col-${col.id}-list`}>
        {items.length === 0 ? (
          <div className="text-[12px] text-slate-400 py-8 text-center border border-dashed border-slate-200 bg-slate-50/40">
            Niente qui per ora.
          </div>
        ) : items.map(idea => (
          <KanbanCard
            key={idea.idea_id}
            idea={idea}
            onVote={(direction) => onVote(idea, direction)}
            voting={votingId === idea.idea_id}
            isOps={isOps}
            onAdminStatus={(s) => onAdminStatus(idea, s)}
            onAdminPriority={(p) => onAdminPriority(idea, p)}
            onAdminEdit={() => onAdminEdit(idea)}
            onAdminDelete={() => onAdminDelete(idea)}
            onOpenDetail={() => onOpenDetail(idea)}
          />
        ))}
      </div>
    </section>
  );
}

function KanbanCard({ idea, onVote, voting, isOps, onAdminStatus, onAdminPriority, onAdminEdit, onAdminDelete, onOpenDetail }) {
  const isClosed = idea.status === 'done' || idea.status === 'discarded';
  const isDiscarded = idea.status === 'discarded';
  const nextPriority = (p) => PRIORITIES[(PRIORITIES.indexOf(p) + 1) % PRIORITIES.length];

  return (
    <article
      className={`group bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all ${isClosed ? 'opacity-80' : ''}`}
      data-testid={`roadmap-idea-${idea.idea_id}`}
    >
      <div className="grid grid-cols-[auto_1fr] gap-2.5 p-3">
        {/* Vote stack: ▲ count ▼ — colorano in base allo stato del voter.
            Stato neutrale: bordi grigi. Up: bordo nero pieno. Down: bordo
            rosso pieno. Solo uno dei due può essere attivo per volta. */}
        <div className="flex flex-col items-stretch min-w-[44px]" data-testid={`roadmap-votes-${idea.idea_id}`}>
          <button
            onClick={(e) => { e.stopPropagation(); onVote(1); }}
            disabled={voting || isClosed}
            className={`flex items-center justify-center py-1 border transition-all ${
              isClosed
                ? 'border-slate-200 bg-slate-50 cursor-not-allowed text-slate-300'
                : idea.has_voted
                  ? 'border-black bg-black text-white'
                  : 'border-slate-200 hover:border-slate-700 hover:bg-slate-50 text-slate-600'
            }`}
            data-testid={`roadmap-vote-${idea.idea_id}`}
            title={isClosed ? 'Idea chiusa' : idea.has_voted ? 'Hai votato a favore — clic per togliere' : 'A favore'}
          >
            <ChevronUp className={`w-3.5 h-3.5 ${voting ? 'animate-pulse' : ''}`} strokeWidth={2.5} />
          </button>
          <div className={`flex items-center justify-center py-1 text-xs font-mono tabular-nums font-semibold border-l border-r ${
            idea.votes < 0 ? 'border-red-200 text-red-600 bg-red-50' :
            idea.votes > 0 ? 'border-slate-200 text-slate-900' :
            'border-slate-200 text-slate-400'
          }`}>
            {idea.votes}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onVote(-1); }}
            disabled={voting || isClosed}
            className={`flex items-center justify-center py-1 border transition-all ${
              isClosed
                ? 'border-slate-200 bg-slate-50 cursor-not-allowed text-slate-300'
                : idea.has_downvoted
                  ? 'border-red-600 bg-red-600 text-white'
                  : 'border-slate-200 hover:border-red-500 hover:bg-red-50 hover:text-red-600 text-slate-400'
            }`}
            data-testid={`roadmap-downvote-${idea.idea_id}`}
            title={isClosed ? 'Idea chiusa' : idea.has_downvoted ? 'Hai votato contro — clic per togliere' : 'Contrario'}
          >
            <ChevronDown className={`w-3.5 h-3.5 ${voting ? 'animate-pulse' : ''}`} strokeWidth={2.5} />
          </button>
        </div>

        <div
          className="min-w-0 cursor-pointer"
          onClick={onOpenDetail}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenDetail(); } }}
          data-testid={`roadmap-open-detail-${idea.idea_id}`}
        >
          {/* Top row: priorità OPS + titolo */}
          <div className="flex items-start gap-1.5 mb-1.5">
            {isOps && idea.priority && (
              <button
                onClick={(e) => { e.stopPropagation(); onAdminPriority(nextPriority(idea.priority)); }}
                className={`inline-flex items-center justify-center px-1 py-0 text-[9px] font-bold border ${PRIORITY_CLS[idea.priority]} hover:opacity-80 flex-shrink-0`}
                title={`Priorità ${idea.priority} — clic per cambiare`}
                data-testid={`roadmap-priority-${idea.idea_id}`}
              >
                {idea.priority}
              </button>
            )}
            <h3 className={`font-heading font-bold text-[13px] text-slate-900 leading-snug flex-1 group-hover:text-black transition-colors ${isDiscarded ? 'line-through' : ''}`}>
              {idea.title}
            </h3>
          </div>

          {idea.description && (
            <p className="text-[12px] text-slate-600 mt-1 leading-snug line-clamp-3">{idea.description}</p>
          )}

          {/* Footer row: meta + admin actions */}
          <div className="flex items-center justify-between gap-2 mt-2.5">
            <p className="text-[10px] text-slate-400 truncate flex items-center gap-2">
              <span className="truncate">
                {idea.status === 'done' && idea.completed_at
                  ? <>Completata <span className="text-emerald-700 font-medium">{fmtRelative(idea.completed_at)}</span></>
                  : <>{idea.author_name} · {fmtRelative(idea.created_at)}</>
                }
              </span>
              {idea.comments_count > 0 && (
                <span
                  className="inline-flex items-center gap-0.5 text-slate-500 flex-shrink-0"
                  title={`${idea.comments_count} ${idea.comments_count === 1 ? 'commento' : 'commenti'}`}
                  data-testid={`roadmap-comments-count-${idea.idea_id}`}
                >
                  <MessageCircle className="w-3 h-3" strokeWidth={2} />
                  <span className="font-mono tabular-nums">{idea.comments_count}</span>
                </span>
              )}
            </p>
            {isOps && (
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                <select
                  value={idea.status}
                  onChange={(e) => { e.stopPropagation(); onAdminStatus(e.target.value); }}
                  onClick={(e) => e.stopPropagation()}
                  className="text-[9px] uppercase tracking-wider font-semibold border border-slate-200 px-1 py-0.5 cursor-pointer hover:border-slate-400"
                  data-testid={`roadmap-status-${idea.idea_id}`}
                  title="Cambia stato"
                >
                  {ALL_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <button
                  onClick={(e) => { e.stopPropagation(); onAdminEdit(); }}
                  className="p-1 hover:bg-slate-100 text-slate-500"
                  title="Modifica"
                  data-testid={`roadmap-edit-${idea.idea_id}`}
                >
                  <Pencil className="w-3 h-3" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onAdminDelete(); }}
                  className="p-1 hover:bg-red-50 text-red-600"
                  title="Elimina"
                  data-testid={`roadmap-delete-${idea.idea_id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function DetailDialog({ idea, onClose, onVote, voting, isOps, onAdminEdit, onAdminDelete, onPrev, onNext, position }) {
  // Solo Esc per chiudere — le frecce per cambiare card stanno nella UI
  // (l'utente ha esplicitamente chiesto di NON usare ← / → da tastiera).
  useEffect(() => {
    if (!idea) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [idea, onClose]);

  if (!idea) return null;
  const isClosed = idea.status === 'done' || idea.status === 'discarded';
  const pill = STATUS_PILL[idea.status] || STATUS_PILL.proposed;
  return (
    <div
      className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 animate-in fade-in"
      onClick={onClose}
      data-testid="roadmap-detail-dialog"
    >
      <div
        className="bg-white w-full max-w-2xl h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top bar: status pill + actions OPS + nav prev/next + close */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isOps && idea.priority && (
              <span className={`inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold border ${PRIORITY_CLS[idea.priority]}`}>
                {idea.priority}
              </span>
            )}
            <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border ${pill.cls}`}>
              {pill.label}
            </span>
            <span className="text-xs text-slate-400">
              {idea.status === 'done' && idea.completed_at
                ? <>Completata {fmtRelative(idea.completed_at)}</>
                : <>{idea.author_name} · {fmtRelative(idea.created_at)}</>
              }
            </span>
            {position && position.total > 1 && (
              <span
                className="text-[10px] font-mono tabular-nums text-slate-400 ml-1"
                data-testid="roadmap-detail-position"
                title="Posizione nell'elenco corrente"
              >
                {position.current} / {position.total}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {isOps && (
              <>
                <button
                  onClick={onAdminEdit}
                  className="p-1.5 text-slate-500 hover:bg-slate-100"
                  title="Modifica"
                  data-testid="roadmap-detail-edit"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={onAdminDelete}
                  className="p-1.5 text-red-600 hover:bg-red-50"
                  title="Elimina"
                  data-testid="roadmap-detail-delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
            {/* Frecce prev/next nella UI del popup. Visibili sempre quando si
                può scorrere; disabled (non hidden) ai bordi per non far
                "saltare" il layout della top bar. */}
            {(onPrev || onNext || (position && position.total > 1)) && (
              <div className="flex items-center mr-1 ml-1 border border-slate-200">
                <button
                  onClick={onPrev || undefined}
                  disabled={!onPrev}
                  className="p-1.5 text-slate-600 hover:bg-slate-100 disabled:text-slate-300 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  title="Idea precedente"
                  data-testid="roadmap-detail-prev"
                  aria-label="Idea precedente"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={onNext || undefined}
                  disabled={!onNext}
                  className="p-1.5 text-slate-600 hover:bg-slate-100 disabled:text-slate-300 disabled:cursor-not-allowed disabled:hover:bg-transparent border-l border-slate-200"
                  title="Idea successiva"
                  data-testid="roadmap-detail-next"
                  aria-label="Idea successiva"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
            <button
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-700"
              title="Chiudi"
              data-testid="roadmap-detail-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body: vote stack a sinistra, titolo + descrizione full a destra.
            `flex-1 min-h-0` permette alla colonna interna di scorrere senza
            far crescere il dialog → header e frecce restano nella stessa
            posizione cambiando idea. Niente overflow sul container esterno:
            ogni sezione (descrizione / commenti) gestisce il proprio scroll
            così le aree sono *visivamente* identiche tra idee diverse. */}
        <div className="flex gap-5 p-6 flex-1 min-h-0">
          <div className="flex flex-col items-stretch min-w-[52px] flex-shrink-0">
            <button
              onClick={() => onVote(1)}
              disabled={voting || isClosed}
              className={`flex items-center justify-center py-1.5 border transition-all ${
                isClosed
                  ? 'border-slate-200 bg-slate-50 cursor-not-allowed text-slate-300'
                  : idea.has_voted
                    ? 'border-black bg-black text-white'
                    : 'border-slate-200 hover:border-slate-700 hover:bg-slate-50 text-slate-600'
              }`}
              data-testid="roadmap-detail-vote"
            >
              <ChevronUp className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <div className={`flex items-center justify-center py-1.5 text-sm font-mono tabular-nums font-semibold border-l border-r ${
              idea.votes < 0 ? 'border-red-200 text-red-600 bg-red-50' :
              idea.votes > 0 ? 'border-slate-200 text-slate-900' :
              'border-slate-200 text-slate-400'
            }`}>
              {idea.votes}
            </div>
            <button
              onClick={() => onVote(-1)}
              disabled={voting || isClosed}
              className={`flex items-center justify-center py-1.5 border transition-all ${
                isClosed
                  ? 'border-slate-200 bg-slate-50 cursor-not-allowed text-slate-300'
                  : idea.has_downvoted
                    ? 'border-red-600 bg-red-600 text-white'
                    : 'border-slate-200 hover:border-red-500 hover:bg-red-50 hover:text-red-600 text-slate-400'
              }`}
              data-testid="roadmap-detail-downvote"
            >
              <ChevronDown className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </div>

          <div className="min-w-0 flex-1 flex flex-col min-h-0">
            <h2 className={`font-heading text-2xl font-bold tracking-tight text-slate-900 mb-3 leading-snug flex-shrink-0 ${idea.status === 'discarded' ? 'line-through' : ''}`}>
              {idea.title}
            </h2>

            {/* Box descrizione a dimensione fissa (h-32 = 128px ≈ 6 righe).
                Scroll interno se la descrizione è lunga; placeholder italic
                quando manca. La altezza identica garantisce che la pill, il
                titolo e i commenti restino sempre alla stessa Y nel popup.
                Bordo a sinistra + ombra interna sul fondo per comunicare
                visivamente che il contenuto è scrollabile quando overflowa. */}
            <div className="flex-shrink-0 h-32 overflow-y-auto pr-3 border-l-2 border-slate-200 pl-3 [box-shadow:inset_0_-12px_12px_-12px_rgba(15,23,42,0.10)]" data-testid="roadmap-detail-description">
              {idea.description ? (
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {idea.description}
                </p>
              ) : (
                <p className="text-sm text-slate-400 italic">Nessuna descrizione fornita.</p>
              )}
            </div>

            {/* Thread commenti pubblici. Caricati pigri al primo render del
                popup. Un commento dà contesto utile a OPS (perché vorresti
                questa feature?) che il solo voto non trasmette.
                Wrapper `flex-1 min-h-0 overflow-y-auto` riempie il resto
                dello spazio col proprio scroll → l'area complessiva del
                popup è completamente fissa anche con thread lunghi. */}
            <div className="flex-1 min-h-0 overflow-y-auto mt-6 pt-5 border-t border-slate-200">
              <CommentsThread
                ideaId={idea.idea_id}
                isClosed={isClosed}
                isOps={isOps}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SubmitDialog({ open, onClose, onSaved }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setTitle(''); setDescription(''); setName(''); setEmail('');
        setErr(null); setSuccess(false);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  const submit = async () => {
    if (!title.trim()) { setErr('Il titolo è obbligatorio'); return; }
    setSaving(true); setErr(null);
    try {
      await roadmapApi.submit({ title: title.trim(), description: description.trim(), name: name.trim(), email: email.trim() });
      setSuccess(true);
      setTimeout(() => onSaved?.(), 1100);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Errore. Riprova.');
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="roadmap-submit-dialog">
      <div className="bg-white max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-start justify-between">
          <div>
            <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
              <MessageSquarePlus className="w-5 h-5" /> Proponi un'idea
            </h2>
            <p className="text-sm text-slate-500 mt-1">Il team la vedrà subito nel backlog interno.</p>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        {success ? (
          <div className="p-8 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
            <p className="font-semibold text-slate-900">Idea inviata!</p>
            <p className="text-sm text-slate-500 mt-1">Grazie. La aggiorneremo qui sopra appena la valutiamo.</p>
          </div>
        ) : (
          <>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Titolo *</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Es. Esporta i KPI in Google Sheets"
                  className="w-full px-3 py-2 border border-slate-300 focus:border-slate-900 focus:outline-none text-sm"
                  data-testid="roadmap-submit-title"
                  autoFocus
                  maxLength={200}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Descrizione</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Aiutaci a capire il contesto: chi la userebbe? In quale momento?"
                  className="w-full px-3 py-2 border border-slate-300 focus:border-slate-900 focus:outline-none text-sm min-h-[100px]"
                  data-testid="roadmap-submit-description"
                  maxLength={4000}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Nome <span className="text-slate-300 normal-case font-normal">(opzionale)</span></label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 focus:border-slate-900 focus:outline-none text-sm"
                    data-testid="roadmap-submit-name"
                    maxLength={80}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Email <span className="text-slate-300 normal-case font-normal">(opzionale)</span></label>
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="per essere avvisato"
                    className="w-full px-3 py-2 border border-slate-300 focus:border-slate-900 focus:outline-none text-sm"
                    data-testid="roadmap-submit-email"
                    maxLength={120}
                    type="email"
                  />
                </div>
              </div>
              {err && (
                <div className="px-3 py-2 border border-red-200 bg-red-50 text-xs text-red-800">{err}</div>
              )}
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={onClose} disabled={saving} className="px-4 py-2 text-sm border border-slate-300 hover:bg-slate-50">Annulla</button>
              <button onClick={submit} disabled={saving} className="px-4 py-2 text-sm bg-black text-white hover:bg-slate-900 disabled:bg-slate-400" data-testid="roadmap-submit-save">
                {saving ? 'Invio…' : 'Invia idea'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Thread di commenti pubblici per un'idea. Gestisce caricamento iniziale,
 * form di submit (anonimo per default, con name/email opzionali) e — se OPS
 * è loggato — bottone elimina per moderazione.
 *
 * Il fetch avviene al mount: quando il popup si apre, `ideaId` cambia e il
 * componente viene ricostruito da zero. Volutamente niente cache lato client
 * per evitare race condition tra un commento appena postato e dati staleness
 * di un'altra idea.
 */
function CommentsThread({ ideaId, isClosed, isOps }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [posting, setPosting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    roadmapApi.listComments(ideaId)
      .then(r => { if (!cancelled) setComments(r.data || []); })
      .catch(() => { if (!cancelled) setComments([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ideaId]);

  const submit = async () => {
    const t = text.trim();
    if (!t) { setErr('Scrivi qualcosa prima di inviare'); return; }
    if (t.length > 2000) { setErr('Massimo 2000 caratteri'); return; }
    setPosting(true); setErr(null);
    try {
      const r = await roadmapApi.addComment(ideaId, { text: t, name: name.trim() });
      setComments(prev => [...prev, r.data]);
      setText('');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Invio fallito, riprova.');
    } finally { setPosting(false); }
  };

  const removeComment = async (commentId) => {
    if (!window.confirm('Eliminare definitivamente questo commento?')) return;
    try {
      await roadmapApi.deleteComment(ideaId, commentId);
      setComments(prev => prev.filter(c => c.comment_id !== commentId));
    } catch (e) {
      alert(e.response?.data?.detail || 'Eliminazione fallita');
    }
  };

  return (
    <div data-testid="roadmap-comments-thread">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 mb-3 flex items-center gap-2">
        <MessageCircle className="w-3.5 h-3.5" strokeWidth={2} />
        Commenti
        {comments.length > 0 && (
          <span className="font-mono tabular-nums text-slate-400 normal-case tracking-normal">
            ({comments.length})
          </span>
        )}
      </h3>

      {loading ? (
        <div className="py-3 text-xs text-slate-400">Caricamento…</div>
      ) : comments.length === 0 ? (
        <p className="text-xs text-slate-400 italic mb-4">
          Nessun commento ancora. Sii il primo a spiegare perché ti interessa.
        </p>
      ) : (
        <ul className="space-y-3 mb-5" data-testid="roadmap-comments-list">
          {comments.map(c => (
            <li
              key={c.comment_id}
              className="border-l-2 border-slate-200 pl-3 group"
              data-testid={`roadmap-comment-${c.comment_id}`}
            >
              <div className="flex items-baseline justify-between gap-2 mb-0.5">
                <span className="text-[11px] font-semibold text-slate-700">
                  {c.author_name || 'Utente anonimo'}
                </span>
                <span className="text-[10px] text-slate-400 flex items-center gap-2">
                  {fmtRelative(c.created_at)}
                  {isOps && (
                    <button
                      onClick={() => removeComment(c.comment_id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 text-red-500 hover:text-red-700"
                      title="Elimina commento"
                      data-testid={`roadmap-comment-delete-${c.comment_id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </span>
              </div>
              <p className="text-[13px] text-slate-700 whitespace-pre-wrap leading-snug">{c.text}</p>
            </li>
          ))}
        </ul>
      )}

      {!isClosed ? (
        <div className="space-y-2" data-testid="roadmap-comment-form">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Spiega perché ti interessa…"
            className="w-full px-3 py-2 border border-slate-300 focus:border-slate-900 focus:outline-none text-sm min-h-[70px]"
            maxLength={2000}
            data-testid="roadmap-comment-input"
          />
          <div className="flex items-center gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome (opzionale)"
              className="flex-1 px-3 py-1.5 border border-slate-300 focus:border-slate-900 focus:outline-none text-xs"
              maxLength={80}
              data-testid="roadmap-comment-name"
            />
            <button
              onClick={submit}
              disabled={posting || !text.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-black text-white hover:bg-slate-900 disabled:bg-slate-300 disabled:cursor-not-allowed"
              data-testid="roadmap-comment-submit"
            >
              <Send className="w-3 h-3" /> {posting ? 'Invio…' : 'Invia'}
            </button>
          </div>
          {err && (
            <div className="px-3 py-1.5 border border-red-200 bg-red-50 text-[11px] text-red-800">{err}</div>
          )}
        </div>
      ) : (
        <p className="text-[11px] text-slate-400 italic">
          I commenti sono chiusi su idee già definite.
        </p>
      )}
    </div>
  );
}

